import { useEffect, useRef } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import { subscribe } from 'lib/pusher';
import { ltdRefreshTriggerAtom, toastAtom } from 'pages/type-curves/atoms';
import { describeRegenerationScope, unitRegenerationAtom } from './atoms';

/** Fired on the unit channel once per zone run as it reaches a terminal status (BE constant). */
export const ZONE_COMPLETED_EVENT =
  'inventory_unit_regeneration.zone.completed';

/** How long the terminal banner stays up before clearing itself. */
const DONE_LINGER_MS = 6000;

interface ZoneCompletedEvent {
  requestId?: number;
  unitId?: number;
  formation?: string | null;
  status?: string;
}

/**
 * Follows a unit's slot regeneration (kicked off by a rotate / undo) over Pusher and advances
 * `unitRegenerationAtom` as each formation finishes.
 *
 * Mount ONCE on the page: it holds the single subscription to the unit channel the rotate response
 * handed back, so the modal banner and the main-page banner both read one tally rather than each
 * subscribing. Zone events only advance the tally; the slot surfaces (gun barrel, slot table, map
 * slots) are refreshed ONCE via `ltdRefreshTriggerAtom` when the last zone reports — refreshing per
 * zone re-fetched everything N times, each cancelling the last. At that point the banner turns
 * terminal, toasts, and clears itself after a moment; until then the rotate and Generate buttons
 * show busy (`unitRegeneratingAtom`).
 */
export function useUnitRegenerationProgress(): void {
  const [regeneration, setRegeneration] = useAtom(unitRegenerationAtom);
  const setLtdRefreshTrigger = useSetAtom(ltdRefreshTriggerAtom);
  const setToast = useSetAtom(toastAtom);
  // Which channel the live subscription is on, so a re-render with the same regeneration does not
  // resubscribe, and a NEW regeneration (different channel, or the same unit rotated again) swaps
  // the subscription over cleanly.
  const channelRef = useRef<{
    name: string;
    channel: ReturnType<typeof subscribe>;
  } | null>(null);
  const lingerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const channelName =
    regeneration && !regeneration.done ? regeneration.channel : null;

  useEffect(() => {
    if (!channelName) return undefined;
    if (channelRef.current?.name === channelName) return undefined;

    // Swap off any previous unit's channel first.
    channelRef.current?.channel.unbind(ZONE_COMPLETED_EVENT);
    channelRef.current?.channel.unsubscribe();

    const channel = subscribe(channelName);
    channelRef.current = { name: channelName, channel };

    channel.bind(ZONE_COMPLETED_EVENT, (evt: ZoneCompletedEvent) => {
      // Only the tally advances here. The slot surfaces are refreshed ONCE, when every zone has
      // reported (below) — refreshing per zone re-fetched the gun barrel, slot table, map slots
      // and wells N times over, each cancelling the last, which read as a request storm.
      //
      // The tally the operator sees is in FORMATIONS (product ask): each event is one zone run,
      // and a formation counts as done only when every one of its runs has reported (a formation
      // can fan out several — one per package/spacing). `done` still keys on the ZONE total, so
      // the one-time refresh waits for the whole unit even when an event arrives without a
      // formation name and cannot advance any formation's own count.
      setRegeneration((current) => {
        if (!current || current.channel !== channelName) return current;
        const formation = evt.formation ?? null;
        const zonesReported = { ...current.zonesReported };
        if (formation) {
          zonesReported[formation] = (zonesReported[formation] ?? 0) + 1;
        }
        const totalZonesReported = Math.min(
          current.totalZonesReported + 1,
          current.totalZones
        );
        const zoneFailed = evt.status !== 'processed';
        const failedFormations =
          zoneFailed &&
          formation &&
          !current.failedFormations.includes(formation)
            ? [...current.failedFormations, formation]
            : current.failedFormations;
        const completed = Object.entries(current.formationZoneCounts).filter(
          ([name, zones]) => (zonesReported[name] ?? 0) >= zones
        ).length;
        return {
          ...current,
          zonesReported,
          totalZonesReported,
          completed,
          failedFormations,
          failed: failedFormations.length,
          failedZones: current.failedZones + (zoneFailed ? 1 : 0),
          done: totalZonesReported >= current.totalZones,
        };
      });
    });

    return () => {
      channel.unbind(ZONE_COMPLETED_EVENT);
      channel.unsubscribe();
      if (channelRef.current?.name === channelName) channelRef.current = null;
    };
  }, [channelName, setRegeneration]);

  // Terminal: toast the outcome and let the banner linger briefly before clearing.
  const done = regeneration?.done ?? false;
  const failedZones = regeneration?.failedZones ?? 0;
  const totalZones = regeneration?.totalZones ?? 0;
  const totalFormations = regeneration?.totalFormations ?? 0;
  const unitName = regeneration?.unitName ?? null;
  useEffect(() => {
    if (!done) return undefined;

    // Every formation has reported: refresh the slot surfaces (gun barrel, slot table, map slots)
    // once, now that the whole unit is at one bearing.
    setLtdRefreshTrigger((prev) => prev + 1);

    const label = unitName ? `${unitName}: ` : '';
    // "Y inventories for X formations" (product wording, Misha) — same phrase as the banner.
    const scope = describeRegenerationScope(totalZones, totalFormations);
    setToast(
      failedZones === 0
        ? {
            kind: 'success',
            title: `${label}slot inventory regenerated`,
            subtitle: `All ${scope} re-laid at the new azimuth.`,
          }
        : {
            kind: 'error',
            title: `${label}slot regeneration finished with failures`,
            subtitle: `${failedZones} of ${scope} did not regenerate.`,
          }
    );

    lingerRef.current = setTimeout(() => {
      setRegeneration((current) => (current?.done ? null : current));
      lingerRef.current = null;
    }, DONE_LINGER_MS);

    return () => {
      if (lingerRef.current) {
        clearTimeout(lingerRef.current);
        lingerRef.current = null;
      }
    };
  }, [
    done,
    failedZones,
    totalZones,
    totalFormations,
    unitName,
    setToast,
    setRegeneration,
    setLtdRefreshTrigger,
  ]);
}
